import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swords, Download, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LeadStatusControl } from '../components/orders/TradeInLeadBanner';

const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });

const BrandTable = ({ rep }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="competitor-brand-table">
      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b">
        <th className="text-left py-2 pr-3">Brand</th>{rep.months.map(m => <th key={m} className="text-right px-2">{monthLabel(m)}</th>)}<th className="text-right pl-3">Total</th>
      </tr></thead>
      <tbody>
        {rep.brands.map(b => (
          <tr key={b.brand} className="border-b border-slate-100" data-testid={`competitor-brand-row-${b.brand}`}>
            <td className="py-2 pr-3 font-semibold">{b.brand}</td>
            {rep.months.map(m => <td key={m} className={`text-right px-2 mono ${b.counts[m] ? 'text-slate-900' : 'text-slate-300'}`}>{b.counts[m] || '·'}</td>)}
            <td className="text-right pl-3 mono font-bold">{b.total}</td>
          </tr>
        ))}
        <tr className="bg-slate-50 font-semibold"><td className="py-2 pr-3">All brands</td>{rep.months.map(m => <td key={m} className="text-right px-2 mono">{rep.totals[m] || '·'}</td>)}<td className="text-right pl-3 mono" data-testid="competitor-grand-total">{rep.brands.reduce((a, b) => a + b.total, 0)}</td></tr>
      </tbody>
    </table>
  </div>
);

const LeadList = ({ leads, onSaved }) => (
  <div className="divide-y divide-slate-100" data-testid="trade-in-lead-list">
    {leads.map(l => (
      <div key={l.key} className="py-3 grid md:grid-cols-12 gap-3 items-start" data-testid={`trade-in-lead-${l.key}`}>
        <div className="md:col-span-4">
          <div className="font-semibold">{l.customer || '—'}</div>
          <div className="text-xs text-slate-500">{[l.contact, l.email, l.phone].filter(Boolean).join(' · ') || 'No contact details'}</div>
        </div>
        <div className="md:col-span-3 text-xs">
          <div><span className="text-slate-500">Brands:</span> <b>{l.brands.join(', ')}</b></div>
          <div className="text-slate-500">{l.machines} machine(s) · last in {l.last_intake || '—'}</div>
          <div className="mono text-[11px]">{l.ro_numbers.join(' ')}</div>
        </div>
        <div className="md:col-span-5"><LeadStatusControl lead={l} onSaved={onSaved} /></div>
      </div>
    ))}
  </div>
);

export default function CompetitorRepairsPage() {
  const { api } = useAuth();
  const [months, setMonths] = useState('12');
  const [rep, setRep] = useState(null);
  const [filter, setFilter] = useState('open');

  const load = useCallback(async () => {
    try { const r = await api().get('/reports/competitor-repairs', { params: { months } }); setRep(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Report failed to load'); }
  }, [api, months]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const r = await api().get('/reports/competitor-repairs', { params: { months, format: 'csv' }, responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `competitor-repairs.csv`; a.click();
  };
  const leads = (rep?.leads || []).filter(l => filter === 'all' || (filter === 'open' ? ['New', 'Contacted'].includes(l.status) : l.status === filter));

  return (
    <div className="space-y-6" data-testid="competitor-repairs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Swords className="w-7 h-7 text-red-600" /> Competitor Repairs</h1>
          <p className="text-sm text-slate-500 mt-1">Competitor machines through <Link to="/orders/repairs" className="underline">Lyphline Repair Orders</Link>, by brand and month. Every customer is a trade-in lead.</p>
        </div>
        <div className="flex gap-2">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="competitor-months-select"><SelectValue /></SelectTrigger>
            <SelectContent>{['6', '12', '24'].map(m => <SelectItem key={m} value={m}>Last {m} months</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} className="rounded-sm" data-testid="competitor-export-csv"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </div>
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Machines by brand</CardTitle></CardHeader>
        <CardContent className="p-4">{!rep ? <p className="text-sm text-slate-400">Loading…</p> : rep.brands.length ? <BrandTable rep={rep} /> : <p className="text-sm text-slate-400 py-6 text-center" data-testid="competitor-empty">No competitor machines in this period yet. Mark a repair order as "Competitor" to start tracking.</p>}</CardContent>
      </Card>
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Trade-in leads ({leads.length})</CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="lead-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="open">Open (New + Contacted)</SelectItem><SelectItem value="all">All</SelectItem>{['New', 'Contacted', 'Won', 'Lost'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-4">{leads.length ? <LeadList leads={leads} onSaved={load} /> : <p className="text-sm text-slate-400 text-center py-4">No leads in this view.</p>}</CardContent>
      </Card>
    </div>
  );
}
